import {
  Box,
  Modal,
  Stack,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useState, useMemo, useEffect } from "react";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import { getMedcardMenus } from "./medicalCardData";
import MedicalCardList from "./MedicalCardList";
import { useSnackbar } from "notistack";
import { referenceService } from "src/api/references/referenceService";
import {
  CreateFavoriteMedicalCardRequestDto,
  DeleteFavoriteMedicalCardPathParamsDto,
  GetFavoriteMedicalCardResponseDto,
} from "src/api/references/referenceServicesDtos";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { isFemale } from "src/utils/gender";

interface MenuModalProps {
  title: string;
  description: string;
  open: boolean;
  onClose: () => void;
  rowID?: string;
  roomNumber?: string;
  age?: string;
  gender?: string;
}

export interface ModalMenu {
  id: string;
  name: string;
  icon?: string;
  url?: string;
  image?: string;
  or_id?: string | number;
  clickFunction?: () => void;
  isFavorite?: boolean;
  favoriteId?: number;
  ageWarning?: string; // Warning message for age restriction
  disabled?: boolean;
  disabledMessage?: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 580, md: 650 },
  maxWidth: 650,
  minHeight: { xs: "80vh", sm: 500, md: 600 },
  maxHeight: "90vh",
  bgcolor: "#fff",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)",
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
};

export const AddMedicalCardModal: React.FC<MenuModalProps> = (props) => {
  const { t } = useTranslate();

  const ageAsNumber = (ageString: string) => {
    const match = ageString.match(/(\d+)\s*yıl/);
    return match ? Number(match[1]) : 0;
  };
  const ageNumber = ageAsNumber(props.age || "0 yıl");

  const [searchTerm, setSearchTerm] = useState("");
  const [favoriteCardsData, setFavoriteCardsData] = useState<ModalMenu[]>([]);
  const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { rowID, roomNumber, age: _age, gender: _gender, ...modalProps } = props;

  const allCards = useMemo(
    () => getMedcardMenus(t, props.rowID as string, Number(ageNumber), props.gender),
    [props.rowID, t, ageNumber, props.gender]
  );

  const fetchFavoriteCards = async () => {
    try {
      const response: GetFavoriteMedicalCardResponseDto =
        await referenceService.favoriteMedicalCard.getFavoriteMedicalCard();

      const mapped: ModalMenu[] = response.data.map((item) => {
        const foundCard = allCards.find(
          (card) => String(card.or_id) === String(item.or_id)
        );

        return {
          id: String(item.id),
          or_id: item.or_id,
          name: foundCard ? foundCard.name : item.name,
          url: foundCard ? foundCard.url : undefined,
          icon: foundCard ? foundCard.icon : undefined,
          image: foundCard ? foundCard.image : undefined,
        };
      });

      setFavoriteCardsData(mapped);
    } catch (error) {
      console.error(t("error-fetching-favorites"), error);
    }
  };

  useEffect(() => {
    if (props.open) {
      fetchFavoriteCards();
      setLoadingButtonId(null);
      setSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (searchTerm !== "") {
      setIsSearching(true);
      const timer = setTimeout(() => setIsSearching(false), 300);
      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm]);

  const sortedAndFilteredCards = useMemo(() => {
    const mergedList = allCards.map((card) => {
      const favoriteEntry = favoriteCardsData.find(
        (fav) => String(fav.or_id) === String(card.or_id)
      );
      return {
        ...card,
        isFavorite: !!favoriteEntry,
        favoriteId: favoriteEntry ? Number(favoriteEntry.id) : undefined,
      };
    });

    const normalizedSearch = searchTerm.trim().toLocaleLowerCase("tr-TR");
    const filtered =
      normalizedSearch === ""
        ? mergedList
        : mergedList.filter((card) =>
          card.name.toLocaleLowerCase("tr-TR").includes(normalizedSearch)
        );

    return filtered.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;

      return a.name.localeCompare(b.name, "tr-TR");
    });
  }, [allCards, favoriteCardsData, searchTerm]);

  const handleToggleFavorite = async (card: ModalMenu) => {
    try {
      if (card.isFavorite) {
        const favoriteEntry = favoriteCardsData.find(
          (fav) => String(fav.or_id) === String(card.or_id)
        );

        if (favoriteEntry && favoriteEntry.id) {
          const request: DeleteFavoriteMedicalCardPathParamsDto = {
            id: parseInt(favoriteEntry.id),
          };
          await referenceService.favoriteMedicalCard.deleteFavoriteMedicalCard(
            request
          );
          enqueueSnackbar(t("favorite-removed"), { variant: "success" });
        }

      } else {
        const request: CreateFavoriteMedicalCardRequestDto = { or_id: Number(card.or_id) };
        await referenceService.favoriteMedicalCard.createFavoriteMedicalCard(
          request
        );
        enqueueSnackbar(t("favorite-added"), { variant: "success" });
      }

      fetchFavoriteCards();
    } catch (error) {
      enqueueSnackbar(t("error-favorite-operation"), { variant: "error" });
      console.error(t("error-favorite-operation"), error);
    }
  };

  const renderEmptyState = (message: string, subMessage: string) => (
    <Box
      sx={{
        textAlign: "center",
        padding: "48px 20px",
        color: "text.secondary",
        minHeight: { xs: 200, sm: 250, md: 300 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          backgroundColor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <SearchIcon sx={{ fontSize: 28, color: "text.disabled" }} />
      </Box>
      <ByzMuiTypography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
        {message}
      </ByzMuiTypography>
      <ByzMuiTypography variant="body2" sx={{ color: "text.disabled" }}>
        {subMessage}
      </ByzMuiTypography>
    </Box>
  );
  const avatarIcon = isFemale(props.gender)
    ? "/assets/icons/pages/nurse/patient-data/icons/user-woman-icon.svg"
    : "/assets/icons/pages/nurse/patient-data/icons/user.svg";

  return (
    <Modal {...modalProps}>
      <Box sx={style}>
        {/* Row 1: Title and Close Button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: "20px 28px",
            backgroundColor: "background.paper",
          }}
        >
          <ByzMuiTypography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "16px",
              marginLeft: "0px",
              fontStyle: "bold",
            }}
          >
            {t("medical-card-add")}
          </ByzMuiTypography>
          <ByzMuiCloseButton onClick={props.onClose} />
        </Stack>

        {/* Row 2: Patient Info */}
        <Box
          sx={{
            padding: "20px 28px",
            backgroundColor: "rgba(224, 233, 245, 1)",
            borderBottom: "1px solid rgba(224, 233, 245, 1)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={avatarIcon}
              alt="Patient Avatar"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />
            <Box>
              <ByzMuiTypography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.5px",
                  flex: 1,
                }}
              >
                {props.title}
              </ByzMuiTypography>
              <ByzMuiTypography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.5px",
                  flex: 1,
                }}
              >
                {props.description}
              </ByzMuiTypography>
              {props.roomNumber && (
                <ByzMuiTypography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "-0.5px",
                    flex: 1,
                  }}
                >
                  {props.roomNumber}
                </ByzMuiTypography>
              )}
            </Box>
          </Stack>
        </Box>
        <Box
          sx={{
            padding: "24px 28px 20px 28px",
            backgroundColor: "background.paper",
          }}
        >
          <ByzMuiTextField
            placeholder={t("medical-card-search")}
            type="search"
            variant="outlined"
            fullWidth
            size="medium"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&.Mui-focused": {
                  backgroundColor: "background.paper",
                  boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: isSearching ? "primary.main" : "text.secondary",
                      fontSize: 22,
                      transition: "color 0.2s ease",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            minHeight: { xs: 250, sm: 300, md: 400 },
            maxHeight: "55vh",
            overflow: "auto",
            backgroundColor: "background.default",
            padding: "10px 28px 28px 28px",
          }}
        >
          {isSearching ? (
            <Stack spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    padding: "16px 20px",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={20}
                    width="70%"
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rectangular" height={16} width="50%" />
                </Box>
              ))}
            </Stack>
          ) : sortedAndFilteredCards.length === 0 ? (
            renderEmptyState(
              t("no-results-found"),
              t("try-changing-search-criteria")
            )
          ) : (
            <MedicalCardList
              cards={sortedAndFilteredCards}
              onToggleFavorite={handleToggleFavorite}
              loadingButtonId={loadingButtonId}
              setLoadingButtonId={setLoadingButtonId}
              onClose={props.onClose}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};
